// =============================================================================
// MOCK — placeholder, replace when real backend exists.
// -----------------------------------------------------------------------------
// EVERY synthetic / placeholder value in the Executive Brain UI lives HERE and
// ONLY here. Real selectors (lib/selectors.js) return real-or-null and NEVER
// fabricate. The UI reads real first and falls back to these placeholders for the
// gaps, so swapping in a real backend later needs NO UI refactor — delete the field
// here and the real value flows through.
//
// Provenance of each block below is the coordinator's PLACEHOLDER list. Do not move
// any of these into a real selector or calculation.
// =============================================================================

// Per-decision-card extras the queue does not provide (confidence %, ETA, risk).
// Indexed by rank-1..3 position.
export const MOCK_DECISION_EXTRAS = [
  { confidencePct: 92, timeToImpact: "~2 days", risk: "LOW", sparkline: [3, 5, 4, 6, 7, 6, 8] },
  { confidencePct: 78, timeToImpact: "~1 week", risk: "MEDIUM", sparkline: [5, 4, 4, 3, 4, 5, 4] },
  { confidencePct: 63, timeToImpact: "~3 days", risk: "MEDIUM", sparkline: [2, 3, 3, 4, 3, 4, 5] },
];

// Company Health sub-dimensions + revenue impact (no real backing yet).
export const MOCK_HEALTH = {
  subdims: [
    { key: "financial", label: "Financial", value: 88 },
    { key: "team", label: "Team", value: 74 },
    { key: "dataQuality", label: "Data Quality", value: 81 },
  ],
  revenueImpact: 128400, // $ — PLACEHOLDER
};

// Momentum percentage deltas + the two synthetic tallies.
export const MOCK_MOMENTUM = {
  deltas: { decisions: +12, experiments: +5, knowledge: +8 }, // % — PLACEHOLDER
  creativeProduced: 47, // PLACEHOLDER
  systemsImproved: 6, // PLACEHOLDER
};

// System Pulse: waveform + Models/Operators statuses (no real per-entity status feed).
export const MOCK_PULSE = {
  waveform: [4, 8, 6, 10, 7, 12, 9, 11, 6, 9, 5, 8, 7, 10, 6],
  models: [
    { name: "Tiny Tim", status: "online" },
    { name: "Claude", status: "online" },
    { name: "Veo 3.1", status: "idle" },
  ],
  operators: [
    { name: "Nick", status: "active" },
    { name: "JC", status: "active" },
    { name: "Ops", status: "idle" },
  ],
};

// Latest Thoughts fallback prose. Prefer REAL moments/observations (selectors);
// these only appear when there is nothing real to say.
export const MOCK_LATEST_THOUGHTS = [
  "Reviewing creative fatigue signals across ABH cohorts.",
  "Cross-checking attribution drift on two AFFILIATE campaigns.",
  "Considering whether to promote a high-confidence prediction to a recommendation.",
];

// Evidence relevance % (the Outcome Spine has no relevance score — placeholder only).
export const MOCK_EVIDENCE_RELEVANCE = [94, 88, 82, 76, 71, 64];

// Decision Review "Notes" tab (no notes backend).
export const MOCK_NOTES = {
  enabled: true,
  placeholder: "Notes are a placeholder — no notes backend exists yet.",
  sample: [],
};

// Cluster fallback counts — used ONLY if a cluster's real summary endpoint is
// disabled/absent, so the constellation never shows a blank. Real counts win.
export const MOCK_CLUSTER_COUNTS = {
  PREDICTION: 0,
  RECOMMENDATION: 0,
  OBSERVATION: 0,
  EXPERIMENT: 0,
  KNOWLEDGE: 0,
  MEMORY: 0,
};

export const MOCK_BANNER = "MOCK — placeholder, replace when real backend exists";

// =============================================================================
// Executive Brain "Cipher's Mind" port — placeholder blocks.
// The capability GRAPH TOPOLOGY (which nodes exist, how they connect) is the
// approved semantic model and lives in viz/CipherGraph.jsx, not here. What lives
// here is only PLACEHOLDER DATA: per-node status/activity where no real count
// exists, the demo proactive events, the demo conversation answers, and a few
// header labels the backend does not yet expose. Real counts (predictions,
// recommendations, observations, experiments, lessons, journal) come through
// selectors and OVERRIDE the placeholder status; activity is always placeholder.
// =============================================================================

// Per-capability status string + activity level (0..1). `status` is used ONLY when
// no real count is available; `activity` has no telemetry yet, so it is always mock.
export const MOCK_CAPABILITY_STATUS = {
  cipher:          { status: "Reasoning",      activity: 1.0 },
  meta:            { status: "Active",         activity: 0.80 },
  shopify:         { status: "Synced",         activity: 0.46 },
  creative:        { status: "Fatigue flag",   activity: 0.52 },
  memory:          { status: "Active",         activity: 0.70 },
  predictions:     { status: "12 active",      activity: 0.78 },
  observations:    { status: "37 new",         activity: 0.82 },
  company:         { status: "82 · Good",      activity: 0.42 },
  google:          { status: "Synced",         activity: 0.50 },
  recommendations: { status: "3 pending",      activity: 0.66 },
  knowledge:       { status: "2,846 insights", activity: 0.64 },
  ghost:           { status: "2 converging",   activity: 0.60 },
  experiments:     { status: "8 running",      activity: 0.56 },
  queue:           { status: "3 waiting",      activity: 0.54 },
};

// Header labels the backend does not expose yet (brain status / system status).
export const MOCK_HEADER = {
  brainStatus: "Optimal",
  systemStatus: "All Systems Operational",
  pipeline: "Realtime",
};

// Decision-card fallback — mirrors the approved artifact's 3 cards. Used ONLY when
// the real /executive/queue is disabled/empty so the review surface still renders.
// sparkline = value series (higher = up). impactNote used when weeklyImpact is null.
export const MOCK_DECISION_CARDS = [
  { rank: 1, title: "Increase AfterBody Meta Budget", accent: "g", weeklyImpact: 4120, confidencePct: 91, sparkline: [3, 4, 4, 5, 6, 6, 8], impactNote: null },
  { rank: 2, title: "Two Ghost Recon Opportunities Are Converging", accent: "o", weeklyImpact: 8700, confidencePct: 77, sparkline: [4, 4, 5, 6, 7, 8, 9], impactNote: null },
  { rank: 3, title: "Creative Fatigue Detected", accent: "p", weeklyImpact: null, confidencePct: 64, sparkline: [8, 7, 7, 6, 5, 4, 3], impactNote: "Impacting performance" },
];

// Proactive demo events — the ONLY simulated proactive content. Each is shaped exactly
// like the event model that will later bind to real executive-queue items, observations,
// recommendations, contradictions, confidence changes, health alerts and system events.
export const MOCK_PROACTIVE_EVENTS = [
  {
    id: "evt-sug-1", level: "suggestion", involved: ["observations", "predictions", "recommendations", "queue"],
    msg: "AfterBody Meta performance has improved while spend remains constrained. I think this deserves review.",
    answer: "Here's the shape of it: CPA is holding 41% under target while budget is capped, so incremental demand is going unspent. I'd lift the AfterBody Meta budget and re-check elasticity in 48 hours. Want me to draft the change for your approval?",
    followups: ["Draft the change", "Why now, not last week?"],
  },
  {
    id: "evt-obs-1", level: "observation", involved: ["observations", "google", "creative"],
    msg: "Meta CPM across AfterBody has climbed 18% over three days — worth knowing before you scale.",
    answer: "It's broad-market, not creative-specific — CPMs are up across every AfterBody ad set, not just the fatigued ones. It doesn't block scaling, it just trims the margin on new spend. I'm keeping watch.",
    followups: ["Show the trend", "Does this change the budget call?"],
  },
  {
    id: "evt-crit-1", level: "critical", involved: ["predictions", "knowledge", "recommendations", "meta"],
    msg: "Two producers disagree on AfterBody budget elasticity — 82% vs 61% confidence. This is gating my top recommendation.",
    answer: "The Financial Critic and PLCIE diverge on how far spend can scale before CPA breaks. Until they reconcile, I'm holding the #1 recommendation at reduced confidence rather than pushing a move I can't fully stand behind. I can show you both sides.",
    followups: ["Show both sides", "Hold it for now", "Override and proceed"],
  },
];

// Conversation demo scenarios — placeholder answers for typed questions. The router
// (lib/proactive.js) matches on `match` keywords; DEFAULT is the fallback.
export const MOCK_CONVERSATION_SCENARIOS = [
  { match: ["top priority", "meta budget", "increase"], path: ["observations", "ghost", "predictions", "knowledge", "recommendations", "queue"],
    answer: "AfterBody's Meta budget is my top priority because it's the highest-confidence, highest-return move available right now — <b>91% confidence</b> on roughly <b>+$4,120/week</b>. Camp B's CPA is running 41% under target and five of five producers agree the elasticity holds.",
    followups: ["Draft the change for approval", "What could go wrong?"] },
  { match: ["overnight", "changed", "change"], path: ["observations", "google", "shopify", "memory"],
    answer: "Three things moved while you slept. Meta CPM rose <b>18%</b> across AfterBody, a new creative angle cleared its early holdout, and Shopify revenue tracked <b>+6%</b> on the day. I folded all three into this morning's ranking.",
    followups: ["Show me the creative angle", "Why does CPM matter here?"] },
  { match: ["concerned", "worried", "risk"], path: ["creative", "experiments", "predictions", "knowledge"],
    answer: "Creative fatigue in the weight-loss set is my main concern — engagement is decaying faster than Experiments can clear replacements. If it holds, it caps the Meta budget upside I'm recommending.",
    followups: ["What replacements are queued?", "How fast is it decaying?"] },
  { match: ["evidence", "supporting", "proof", "why should"], path: ["memory", "knowledge", "predictions", "recommendations"],
    answer: "This rests on <b>2,846</b> validated lessons in Knowledge, <b>12</b> live Predictions, and Camp B's holdout in Experiments. Memory shows five prior budget moves at this elasticity — four paid out.",
    followups: ["Show the one that didn't", "Open the holdout"] },
  { match: ["ghost recon", "ghost", "competitor", "recon"], path: ["ghost", "observations", "predictions", "recommendations"],
    answer: "Ghost Recon is tracking <b>two converging</b> competitor moves in the AfterBody niche. Paired with Observations and Predictions, they point to a short window where added spend compounds — the #2 decision on your board.",
    followups: ["Open decision #2", "Who are the competitors?"] },
  { match: ["explain", "recommendation", "this decision"], path: ["recommendations", "predictions", "knowledge", "memory", "meta"],
    answer: "I ranked every open action on confidence × expected return, then pressure-tested the top one against Knowledge and Memory. It survived all five producer checks — it's what I'd act on first.",
    followups: ["What are the five checks?", "Rank the runners-up"] },
];
// Company-Health ring + sub-rows (no real cross-BU health endpoint yet).
export const MOCK_HEALTH_RING = {
  overall: 82,
  subrows: [
    { label: "Financial Health", v: "Good" },
    { label: "Operational Health", v: "Good" },
    { label: "Team Health", v: "Good" },
    { label: "Integration Health", v: "Good" },
    { label: "Data Quality", v: "Good" },
  ],
};

// Recent Observations fallback rows (used only when the real observations feed is absent).
export const MOCK_RECENT_OBSERVATIONS = [
  { t: "Meta CPM increased 18% in last 3 days", ago: "3m ago" },
  { t: "New creative angle showing promise", ago: "15m ago" },
  { t: "Audience fatigue in weight loss niche", ago: "32m ago" },
];

export const MOCK_CONVERSATION_DEFAULT = {
  path: ["observations", "memory", "knowledge", "predictions", "recommendations"],
  answer: "Here's my read, traced across the graph: I weighed the live signal against what I've learned and what I expect next. This is where I'd focus — ask me to go deeper on any part.",
  followups: ["What changed overnight?", "What are you concerned about?"],
};
